import React from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, Lightbulb, Zap, CheckCircle2 } from 'lucide-react';
import { LatexRenderer } from '../../ui/LatexRenderer';
import { Card } from '../../ui/Card';
interface ExplanationCardProps {
  explanation: string;
  distractorExplanation?: string;
  isCorrect: boolean;
  mode?: 'full' | 'compact';
}
// Helper to render explanation steps
const ExplanationSteps = ({
  text
}: {
  text: string;
}) => {
  const steps = text.split('\n').filter(line => line.trim().length > 0);
  if (steps.length <= 1) {
    return <div className="text-base leading-relaxed" data-id="element-1215">
        <LatexRenderer content={text} data-id="element-1216" />
      </div>;
  }
  return <div className="space-y-3" data-id="element-1217">
      {steps.map((step, idx) => <div key={idx} className="flex gap-3 items-start" data-id="element-1218">
          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-white flex items-center justify-center font-bold text-sm border-2 border-current opacity-80 shadow-sm mt-0.5" data-id="element-1219">
            {idx + 1}
          </div>
          <div className="flex-1 text-base leading-relaxed pt-0.5" data-id="element-1220">
            <LatexRenderer content={step} data-id="element-1221" />
          </div>
        </div>)}
    </div>;
};
/**
 * Displays explanations for correct and incorrect answers.
 * Supports both immediate feedback and review modes.
 */
export function ExplanationCard({
  explanation,
  distractorExplanation,
  isCorrect,
  mode = 'full'
}: ExplanationCardProps) {
  if (mode === 'compact') {
    return <div className="bg-indigo-50 p-3 rounded-lg border border-indigo-200" data-id="element-1222">
        <div className="flex items-center gap-2 font-bold text-indigo-800 mb-1.5 text-sm" data-id="element-1223">
          <Zap className="w-4 h-4" data-id="element-1224" />
          Uitleg
        </div>
        <div className="text-indigo-900 text-sm leading-relaxed" data-id="element-1225">
          <LatexRenderer content={explanation} data-id="element-1226" />
        </div>
      </div>;
  }
  return <div className="space-y-4" data-id="element-1227">
      {/* WRONG ANSWER SECTION */}
      {!isCorrect && distractorExplanation && <motion.div initial={{
      opacity: 0,
      scale: 0.98
    }} animate={{
      opacity: 1,
      scale: 1
    }} data-id="element-1228">
          <Card className="border border-rose-200 bg-white overflow-hidden shadow-sm" data-id="element-1229">
            <div className="bg-rose-50 px-4 py-3 border-b border-rose-100 flex items-center gap-2.5" data-id="element-1230">
              <div className="bg-white p-1.5 rounded-lg shadow-sm border border-rose-100" data-id="element-1231">
                <AlertCircle className="w-5 h-5 text-rose-500" data-id="element-1232" />
              </div>
              <h3 className="text-base font-bold text-rose-900" data-id="element-1233">
                Waarom was dat niet goed?
              </h3>
            </div>
            <div className="p-5" data-id="element-1234">
              <div className="text-slate-700 font-medium" data-id="element-1235">
                <ExplanationSteps text={distractorExplanation} data-id="element-1236" />
              </div>
            </div>
          </Card>
        </motion.div>}

      {/* CORRECT ANSWER SECTION */}
      <motion.div initial={{
      opacity: 0,
      scale: 0.98
    }} animate={{
      opacity: 1,
      scale: 1
    }} data-id="element-1237">
        <Card className={`border overflow-hidden shadow-sm ${isCorrect ? 'border-emerald-200 bg-white' : 'border-indigo-200 bg-white'}`} data-id="element-1238">
          <div className={`px-4 py-3 border-b flex items-center gap-2.5 ${isCorrect ? 'bg-emerald-50 border-emerald-100' : 'bg-indigo-50 border-indigo-100'}`} data-id="element-1239">
            <div className="bg-white p-1.5 rounded-lg shadow-sm border border-current opacity-80" data-id="element-1240">
              {isCorrect ? <CheckCircle2 className="w-5 h-5 text-emerald-600" data-id="element-1241" /> : <Lightbulb className="w-5 h-5 text-indigo-600" data-id="element-1242" />}
            </div>
            <h3 className={`text-base font-bold ${isCorrect ? 'text-emerald-900' : 'text-indigo-900'}`} data-id="element-1243">
              {isCorrect ? 'Waarom dit klopt' : 'Hoe je dit oplost'}
            </h3>
          </div>
          <div className="p-5" data-id="element-1244">
            <div className={`font-medium ${isCorrect ? 'text-slate-700' : 'text-slate-800'}`} data-id="element-1245">
              <ExplanationSteps text={explanation || 'Goed gedaan! Je hebt de juiste strategie gebruikt.'} data-id="element-1246" />
            </div>
          </div>
        </Card>
      </motion.div>
    </div>;
}